import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { formatDistance } from "date-fns";
import toast from "react-hot-toast";
import client from "../../../lib/feathers";
import { tierHasFeature } from "../../../lib/featureLevels";

export default function UserPhotosPanel() {
  const listing = useSelector((s) => s.listings.selected);
  const user = useSelector((s) => s.auth.user);
  const hasFeature = tierHasFeature(listing?.subscriptionTier, 'MODERATE_USER_PHOTOS');
  const [photos, setPhotos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const pageSize = 50;

  const apiPath = user.role === 'admin' ? 'admin/listing-user-photos' : 'industry/listing-user-photos';

  const loadPhotos = async () => {
    if (!listing?.id) return;

    try {
      setLoading(true);
      const service = client.service(apiPath);
      const result = await service.find({
        query: { listingId: listing.id, $limit: pageSize, $skip: 0 }
      });
      setPhotos(result.data || []);
      setTotal(result.total || 0);
    } catch (error) {
      console.error('Error loading user photos:', error);
      toast.error('Failed to load user photos');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    try {
      setLoadingMore(true);
      const service = client.service(apiPath);
      const result = await service.find({
        query: { listingId: listing.id, $limit: pageSize, $skip: photos.length }
      });
      setPhotos([...photos, ...(result.data || [])]);
    } catch (error) {
      console.error('Error loading more photos:', error);
      toast.error('Failed to load more photos');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    loadPhotos();
  }, [listing?.id]);

  const handleToggleVisibility = async (photo) => {
    try {
      const service = client.service(apiPath);
      await service.patch(photo.id, {
        galleryHidden: !photo.galleryHidden,
        listingId: listing.id
      });

      // Update local state
      setPhotos(photos.map(p =>
        p.id === photo.id
          ? { ...p, galleryHidden: !p.galleryHidden }
          : p
      ));

      toast.success(
        !photo.galleryHidden
          ? 'Photo hidden from gallery'
          : 'Photo now visible in gallery'
      );
    } catch (error) {
      console.error('Error toggling photo visibility:', error);
      toast.error('Failed to update photo visibility');
    }
  };

  if (loading) {
    return (
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <p className="text-gray-500">Loading photos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          User Photos ({total})
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          Photos contributed by users during check-ins
        </p>
      </div>

      {user.role === 'admin' && !hasFeature && (
        <div className="mx-4 mb-4 px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-800">
            This listing's tier does not include user photo management. The listing owner cannot see or moderate these photos from their dashboard.
          </p>
        </div>
      )}

      {photos.length === 0 ? (
        <div className="px-4 py-5 sm:px-6 text-gray-500">
          No user photos for this listing
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4">
          {photos.map((photo) => (
            <div
              key={photo.id}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="aspect-square relative cursor-pointer" onClick={() => setSelectedPhoto(photo)}>
                <img
                  src={`${process.env.REACT_APP_ATTACHMENT_URL}/${photo.path}?width=300`}
                  alt={photo.caption || 'User photo'}
                  className="w-full h-full object-cover"
                />
                {photo.pendingReportCount > 0 && (
                  <span className="absolute top-2 left-2 bg-red-100 text-red-800 text-xs font-medium px-2 py-0.5 rounded">
                    Reported ({photo.pendingReportCount})
                  </span>
                )}
                {photo.galleryHidden === true && (
                  <span className="absolute top-2 right-2 bg-gray-100 text-gray-800 text-xs font-medium px-2 py-0.5 rounded">
                    Hidden
                  </span>
                )}
              </div>

              <div className="p-3 space-y-2">
                <div className="text-sm font-medium text-gray-900">
                  {photo.user?.firstName} {photo.user?.lastName}
                </div>

                <div className="text-xs text-gray-500">
                  {formatDistance(new Date(photo.createdAt), new Date(), { addSuffix: true })}
                </div>

                {photo.caption && (
                  <div className="text-sm text-gray-700 line-clamp-2">
                    {photo.caption}
                  </div>
                )}

                <button
                  onClick={() => handleToggleVisibility(photo)}
                  className={`w-full mt-2 px-3 py-1.5 text-xs font-medium rounded ${
                    photo.galleryHidden
                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}
                >
                  {photo.galleryHidden ? 'Show in Gallery' : 'Hide from Gallery'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {photos.length < total && (
        <div className="px-4 py-4 text-center">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            {loadingMore ? 'Loading...' : `Load More (${photos.length} of ${total})`}
          </button>
        </div>
      )}

      {selectedPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] mx-4" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute -top-10 right-0 text-white text-sm hover:text-gray-300"
            >
              Close
            </button>
            <img
              src={`${process.env.REACT_APP_ATTACHMENT_URL}/${selectedPhoto.path}`}
              alt={selectedPhoto.caption || 'User photo'}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            <div className="mt-2 text-white text-sm text-center">
              {selectedPhoto.user?.firstName} {selectedPhoto.user?.lastName}
              {selectedPhoto.caption && ` — ${selectedPhoto.caption}`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
